import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import "@/design-system/typography.css";
import '@/design-system/forms.css';
import { colors } from "@/design-system/colors";

type SortDirection = "asc" | "desc";
type SortConfig<T> = {
  columnKey: string;
  direction: SortDirection;
  /** The column's server sort key (`GenericTableColumn.sortKey`), when it has one. */
  sortKey?: string;
  sortValue?: (row: T) => string | number | Date | boolean | null | undefined;
};
type SortState<T> = SortConfig<T> | null;

const formatSize = (value?: string | number) =>
  typeof value === "number" ? `${value}px` : value;

const DEFAULT_COLUMN_MIN_WIDTH = 18;
const CHECKBOX_COLUMN_WIDTH = '1.125rem'; // 18px = 1.125rem

export interface GenericTableColumn<T> {
  key: keyof T | string;
  header: string;
  align?: "left" | "center" | "right";
  width?: string;
  minWidth?: number; // Minimum width in pixels
  flexGrow?: number; // How much this column should grow relative to others (default 1)
  sortable?: boolean;
  /** Server mode: the key the backend sorts by for this column (default: `key`). */
  sortKey?: string;
  render?: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number | Date | boolean | null | undefined;
}

/**
 * Server-driven mode. The table does NOT sort or slice: `data` is exactly the rows to show, `total` is the
 * server's count of ALL matches, and sort clicks / page changes / "load more" are reported to the parent,
 * which asks the API (see hooks/useEntityList.ts).
 */
export interface GenericTableServerMode {
  total: number;
  /** 'pages': a paginator over `page` / `onPageChange`. 'append': rows accumulate; `onLoadMore` fires when the end of the table scrolls into view. */
  mode: 'pages' | 'append';
  hasMore?: boolean;
  onLoadMore?: () => void;
}

export interface GenericTableProps<T> {
  columns: GenericTableColumn<T>[];
  data: T[];
  dense?: boolean;
  zebra?: boolean;
  loading?: boolean;
  emptyState?: React.ReactNode;
  rowKey?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  defaultSort?: SortConfig<T>;
  sort?: SortConfig<T>;
  onSortChange?: (nextSort: SortState<T>) => void;
  pageSize?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  selectable?: boolean;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  scrollContainerMaxWidth?: string | number;
  minTableWidth?: number;
  server?: GenericTableServerMode;
}

const DEFAULT_PAGE_SIZE = 10;

export function GenericTable<T>({
  columns,
  data,
  dense = true,
  zebra = false,
  loading = false,
  emptyState,
  rowKey,
  onRowClick,
  defaultSort,
  sort,
  onSortChange,
  pageSize = DEFAULT_PAGE_SIZE,
  page,
  onPageChange,
  selectable = true,
  selectedKeys,
  onSelectionChange,
  scrollContainerMaxWidth = "100%",
  minTableWidth = 768,
  server,
}: GenericTableProps<T>) {
  const pageSizeValue = Math.max(1, pageSize);
  const [internalSort, setInternalSort] = React.useState<SortState<T>>(null);
  const [internalPage, setInternalPage] = React.useState(1);
  const [internalSelectedKeys, setInternalSelectedKeys] = React.useState<string[]>([]);

  const scrollMaxWidthValue = formatSize(scrollContainerMaxWidth) ?? "100%";
  const tableMinWidthValue = formatSize(minTableWidth) ?? "768px";

  const activeSort = (sort as SortState<T> | undefined) ?? internalSort;
  const selection = selectedKeys ?? internalSelectedKeys;

  React.useEffect(() => {
    if (selectedKeys) {
      setInternalSelectedKeys(selectedKeys);
    }
  }, [selectedKeys]);

  const sortedRows = React.useMemo(() => {
    // Server mode: the rows already arrive in the server's order.
    if (server) return data;
    const sortConfig = activeSort ?? defaultSort ?? null;
    if (!sortConfig) {
      return data;
    }

    const column = columns.find((col) => col.key === sortConfig.columnKey);

    const resolver =
      sortConfig.sortValue ??
      column?.sortValue ??
      ((row: T) => {
        const value = getPrimitiveValue(row, sortConfig.columnKey);
        if (value instanceof Date) return value.getTime();
        if (typeof value === "boolean") return value ? 1 : 0;
        return value ?? "";
      });

    const comparator = (a: T, b: T) => {
      const aVal = resolver(a);
      const bVal = resolver(b);
      if (aVal === bVal) return 0;
      if (aVal == null) return -1;
      if (bVal == null) return 1;
      return aVal > bVal ? 1 : -1;
    };

    return [...data].sort((a, b) =>
      sortConfig.direction === "asc" ? comparator(a, b) : comparator(b, a)
    );
  }, [activeSort, columns, data, defaultSort, server]);

  const totalRows = server ? server.total : sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSizeValue));
  const controlledPage = page ?? internalPage;
  const currentPage = Math.min(Math.max(controlledPage, 1), totalPages);
  const serverAppends = server?.mode === 'append';
  const pageStartIndex = serverAppends ? 0 : (currentPage - 1) * pageSizeValue;
  const paginatedRows = server ? sortedRows : sortedRows.slice(pageStartIndex, pageStartIndex + pageSizeValue);

  // Server "append" mode: reaching the end of the table asks the parent for the next page.
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = React.useRef(server?.onLoadMore);
  onLoadMoreRef.current = server?.onLoadMore;
  const appendWatch = serverAppends && server?.hasMore === true;
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!appendWatch || !el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLoadMoreRef.current?.();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [appendWatch, data.length]);

  React.useEffect(() => {
    if (!page && controlledPage !== currentPage) {
      setInternalPage(currentPage);
    }
  }, [controlledPage, currentPage, page]);

  React.useEffect(() => {
    if (!page && currentPage > totalPages) {
      setInternalPage(totalPages);
    }
  }, [currentPage, page, totalPages]);

  const setSelection = React.useCallback(
    (next: string[]) => {
      onSelectionChange?.(next);
      if (!selectedKeys) {
        setInternalSelectedKeys(next);
      }
    },
    [onSelectionChange, selectedKeys]
  );

  const pageRowKeys = React.useMemo(
    () =>
      paginatedRows.map((row, index) =>
        getRowKeyValue(row as T, pageStartIndex + index, rowKey)
      ),
    [paginatedRows, pageStartIndex, rowKey]
  );

  const headerAllChecked = React.useMemo(() => {
    if (!selectable || pageRowKeys.length === 0) {
      return false;
    }
    return pageRowKeys.every((key) => selection.includes(key));
  }, [pageRowKeys, selectable, selection]);

  const headerIndeterminate = React.useMemo(() => {
    if (!selectable || pageRowKeys.length === 0) {
      return false;
    }
    const selectedCount = pageRowKeys.filter((key) => selection.includes(key)).length;
    return selectedCount > 0 && selectedCount < pageRowKeys.length;
  }, [pageRowKeys, selectable, selection]);

  const headerCheckboxRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = headerIndeterminate;
    }
  }, [headerIndeterminate]);

  const handleSelectAll = (checked: boolean) => {
    if (!selectable || pageRowKeys.length === 0) return;
    if (checked) {
      const next = Array.from(new Set([...selection, ...pageRowKeys]));
      setSelection(next);
    } else {
      setSelection(selection.filter((key) => !pageRowKeys.includes(key)));
    }
  };

  const handleRowCheckbox = (key: string, checked: boolean) => {
    if (checked) {
      if (!selection.includes(key)) {
        setSelection([...selection, key]);
      }
    } else {
      setSelection(selection.filter((value) => value !== key));
    }
  };

  const handleSort = (column: GenericTableColumn<T>) => {
    if (!column.sortable) return;
    const columnKey = String(column.key);
    const isActive = activeSort?.columnKey === columnKey;
    let nextSort: SortState<T>;

    const sortKey = column.sortKey;
    if (!isActive) {
      nextSort = { columnKey, direction: "asc", ...(sortKey ? { sortKey } : {}) };
    } else if (activeSort?.direction === "asc") {
      nextSort = { columnKey, direction: "desc", ...(sortKey ? { sortKey } : {}) };
    } else {
      nextSort = null;
    }

    if (onSortChange) {
      onSortChange(nextSort);
      return;
    }

    setInternalSort(nextSort);
  };

  const getSortIndicator = (columnKey: string) => {
    if (!activeSort || activeSort.columnKey !== columnKey) return null;
    return activeSort.direction === "asc" ? "▲" : "▼";
  };

  const renderCell = (row: T, column: GenericTableColumn<T>) => {
    if (column.render) {
      return column.render(row);
    }
    const value = getPrimitiveValue(row, column.key);
    if (value === null || value === undefined || value === "") {
      return <span className="text-gray-400">—</span>;
    }
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    return String(value);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    if (onPageChange) {
      onPageChange(nextPage);
      return;
    }
    setInternalPage(nextPage);
  };

  const startLabel = paginatedRows.length === 0 ? 0 : pageStartIndex + 1;
  const endLabel = paginatedRows.length === 0 ? 0 : pageStartIndex + paginatedRows.length;
  // Server mode keeps the rows on screen while the next page / sort loads; only an empty table shows skeletons.
  const showSkeleton = loading && (!server || sortedRows.length === 0);


  const getColumnWidth = (column: GenericTableColumn<T>) => {
    // If explicit width is set, use it (e.g., '7.5rem')
    if (column.width) {
      return column.width;
    }
    // For columns without explicit width, don't set width - let table-layout handle it
    return undefined;
  };

  return (
    <div 
      className="rounded-2xl bg-white shadow-[var(--shadow-subtle)] overflow-hidden"
      style={{ border: `1px solid ${colors.tableBorder}` }}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{ maxWidth: scrollMaxWidthValue }}
      >
        <div className="overflow-x-auto">
          <Table
            className={cn(
              dense ? "text-sm" : "text-base",
              "w-full border-collapse rounded-lg"
            )}
            style={{ 
              minWidth: tableMinWidthValue,
              tableLayout: "fixed" // CRITICAL: Forces columns to respect width constraints
            }}
          >
            <TableHeader>
                <TableRow className="bg-table-header hover:bg-table-header">
                {selectable && (
                <TableHead
                  className="px-4 py-2 text-center font-table-head"
                  style={{
                    width: CHECKBOX_COLUMN_WIDTH,
                    minWidth: CHECKBOX_COLUMN_WIDTH,
                    maxWidth: CHECKBOX_COLUMN_WIDTH,
                    whiteSpace: "nowrap",
                  }}
                >
                    <Checkbox
                        aria-label="Select all rows"
                        checked={headerAllChecked}
                        ref={headerCheckboxRef}
                        className="h-3.5 w-3.5"
                        onCheckedChange={(value) => handleSelectAll(Boolean(value))}
                    />
                    </TableHead>
                )}
              {columns.map((column) => {
                    const alignment = textAlignment(column.align);
                const resolvedMinWidth =
                  column.minWidth ?? column.width ?? DEFAULT_COLUMN_MIN_WIDTH;
                const columnMinWidth =
                  typeof resolvedMinWidth === "number"
                    ? `${resolvedMinWidth}px`
                    : resolvedMinWidth;
                const columnWidth = getColumnWidth(column);
                    return (
                    <TableHead
                        key={String(column.key)}
                        style={{
                        whiteSpace: "nowrap",
                        minWidth: columnMinWidth,
                        ...(columnWidth && { width: columnWidth, maxWidth: columnWidth }),
                        }}
                        className={cn(
                        "font-table-head h-10 select-none transition-colors px-4 py-2",
                        alignment,
                        column.sortable && "cursor-pointer hover:text-gray-900"
                        )}
                        onClick={() => handleSort(column)}
                    >
                        <div
                        className={cn(
                            "flex items-center gap-1",
                            column.align === "center" && "justify-center",
                            column.align === "right" && "justify-end"
                        )}
                        >
                        <span>{column.header}</span>
                        {column.sortable && (
                            <span className="text-[length:var(--font-size-2xs)] text-gray-400">{getSortIndicator(String(column.key))}</span>
                        )}
                        </div>
                    </TableHead>
                    );
                })}
                </TableRow>
            </TableHeader>
            <TableBody>
                {!loading && sortedRows.length === 0 && (
                <TableRow>
                    <TableCell
                    colSpan={columns.length + (selectable ? 1 : 0)}
                    className="py-10 text-center text-gray-500"
                    >
                    {emptyState ?? "No records to show."}
                    </TableCell>
                </TableRow>
                )}

                {showSkeleton &&
                Array.from({ length: pageSizeValue }).map((_, idx) => (
                    <TableRow key={`skeleton-${idx}`}>
                    {Array.from({ length: columns.length + (selectable ? 1 : 0) }).map((__, colIdx) => (
                        <TableCell key={`skeleton-${idx}-${colIdx}`}>
                        <div className="h-4 animate-pulse rounded bg-gray-200" />
                        </TableCell>
                    ))}
                    </TableRow>
                ))}

                {!showSkeleton &&
                paginatedRows.map((row, rowIndex) => {
                    const key = getRowKeyValue(row as T, pageStartIndex + rowIndex, rowKey);
                    const isSelected = selection.includes(key);
                    return (
                    <TableRow
                        key={key}
                        className={cn(
                        "transition-colors hover:bg-blue-50",
                        zebra && rowIndex % 2 === 1 ? "bg-gray-50" : "",
                        onRowClick && "cursor-pointer"
                        )}
                        style={{ borderBottom: `1px solid ${colors.tableBorder}` }}
                        onClick={() => onRowClick?.(row as T)}
                    >
                        {selectable && (
                        <TableCell
                            className="px-4 py-2 text-center"
                            style={{
                            width: `${CHECKBOX_COLUMN_WIDTH}px`,
                            minWidth: `${CHECKBOX_COLUMN_WIDTH}px`,
                            maxWidth: `${CHECKBOX_COLUMN_WIDTH}px`,
                            whiteSpace: "nowrap",
                            }}
                        >
                            <Checkbox
                            aria-label={`Select row ${rowIndex + 1}`}
                            checked={isSelected}
                            className="h-3.5 w-3.5"
                            onCheckedChange={(value) => handleRowCheckbox(key, Boolean(value))}
                            onClick={(event) => event.stopPropagation()}
                            />
                        </TableCell>
                        )}
                        {columns.map((column) => {
                          const resolvedMinWidth =
                            column.minWidth ?? column.width ?? DEFAULT_COLUMN_MIN_WIDTH;
                          const columnMinWidth =
                            typeof resolvedMinWidth === "number"
                              ? `${resolvedMinWidth}px`
                              : resolvedMinWidth;
                          const columnWidth = getColumnWidth(column);
                          return (
                        <TableCell
                            key={String(column.key)}
                            className={cn(
                            "font-table-body text-sm text-gray-700 px-4",
                            dense ? "py-2" : "py-3",
                            textAlignment(column.align)
                            )}
                            style={{
                              minWidth: columnMinWidth,
                              ...(columnWidth && { width: columnWidth, maxWidth: columnWidth }),
                            }}
                        >
                            <div 
                              className="truncate"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {renderCell(row as T, column)}
                            </div>
                        </TableCell>
                          );
                        })}
                    </TableRow>
                    );
                })}
            </TableBody>
          </Table>
          </div>
        </div>
        <div 
          className="flex flex-col gap-3 px-4 py-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between"
          style={{ borderTop: `1px solid ${colors.tableBorder}` }}
        >
        <span className="font-table-body text-gray-500">
          Showing {startLabel}-{endLabel} of {totalRows} entries
        </span>
        {serverAppends ? (
          <div ref={sentinelRef} className="font-table-body text-gray-400">
            {loading && sortedRows.length > 0 ? "Loading…" : ""}
          </div>
        ) : (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="hover:bg-gray-100"
          >
            Previous
          </Button>
          <span className="font-table-head text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={currentPage === totalPages || sortedRows.length === 0}
            onClick={() => handlePageChange(currentPage + 1)}
            className="hover:bg-gray-100"
          >
            Next
          </Button>
        </div>
        )}
      </div>
    </div>
  );
}

function textAlignment(align: GenericTableColumn<unknown>["align"]) {
  switch (align) {
    case "center":
      return "text-center";
    case "right":
      return "text-right";
    default:
      return "text-left";
  }
}

function getPrimitiveValue<T>(row: T, key: keyof T | string) {
  const path = String(key).split(".");
  let current: any = row;
  for (const segment of path) {
    if (current == null) return null;
    current = current[segment];
  }
  return current;
}

function getRowKeyValue<T>(row: T, index: number, rowKey?: (row: T, index: number) => string) {
  return rowKey ? rowKey(row, index) : String(index);
}